import { Fragment, type ReactNode, useState } from 'react'

/**
 * РАЗМЕТКА В СООБЩЕНИЯХ
 *
 * Разбираем при показе, а не при отправке: в базе лежит ровно то, что
 * человек написал. Так текст остаётся читаемым откуда угодно, а правила
 * разметки можно менять, не трогая уже отправленное.
 *
 * Поддерживаем то, чем пользуются: жирный, курсив, зачёркнутый,
 * моноширинный, скрытый текст, ссылки и упоминания.
 *
 *   *жирный*   _курсив_   ~зачёркнутый~
 *   `код`      ```блок кода```
 *   ||скрытое||   @имя
 */

type Token =
  | { kind: 'text'; value: string }
  | { kind: 'bold' | 'italic' | 'strike' | 'code' | 'pre' | 'spoiler'; value: string }
  | { kind: 'link'; value: string; href: string }
  | { kind: 'mention'; value: string }

/**
 * Порядок важен: блок кода ищем раньше строчного, иначе ``` разберётся
 * как две пустые вставки. Внутри кода разметка не действует — иначе
 * звёздочку в коде нельзя написать.
 */
const RULES: { kind: Token['kind']; re: RegExp }[] = [
  { kind: 'pre', re: /```([\s\S]+?)```/ },
  { kind: 'code', re: /`([^`\n]+?)`/ },
  { kind: 'spoiler', re: /\|\|([\s\S]+?)\|\|/ },
  { kind: 'bold', re: /(?<![\wа-яё])\*([^*\n]+?)\*(?![\wа-яё])/i },
  { kind: 'italic', re: /(?<![\wа-яё])_([^_\n]+?)_(?![\wа-яё])/i },
  { kind: 'strike', re: /(?<![\wа-яё])~([^~\n]+?)~(?![\wа-яё])/i },
]

// Адрес до первого пробела; хвостовая пунктуация в ссылку не входит
const LINK = /(https?:\/\/[^\s<]+[^\s<.,:;!?)\]}»"'])/
const MENTION = /(?<![\wа-яё@])@([a-zA-Zа-яёА-ЯЁ][\wа-яёА-ЯЁ-]{1,31})/

function tokenize(text: string): Token[] {
  const out: Token[] = []
  let rest = text

  while (rest.length > 0) {
    let best: { index: number; length: number; token: Token } | null = null

    const consider = (index: number, length: number, token: Token) => {
      if (index < 0) return
      if (!best || index < best.index) best = { index, length, token }
    }

    for (const rule of RULES) {
      const match = rule.re.exec(rest)
      if (match && match[1] !== undefined) {
        consider(match.index, match[0].length, { kind: rule.kind, value: match[1] } as Token)
      }
    }

    const link = LINK.exec(rest)
    if (link?.[1]) consider(link.index, link[0].length, { kind: 'link', value: link[1], href: link[1] })

    const mention = MENTION.exec(rest)
    if (mention?.[1]) consider(mention.index, mention[0].length, { kind: 'mention', value: mention[1] })

    if (!best) {
      out.push({ kind: 'text', value: rest })
      break
    }

    const found: { index: number; length: number; token: Token } = best
    if (found.index > 0) out.push({ kind: 'text', value: rest.slice(0, found.index) })
    out.push(found.token)
    rest = rest.slice(found.index + found.length)
  }

  return out
}

/** Скрытый текст: показывается по нажатию, как в мессенджерах. */
function Spoiler({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState(false)
  return (
    <span
      className={'spoiler' + (shown ? ' is-shown' : '')}
      onClick={() => setShown(true)}
      title={shown ? undefined : 'Нажми, чтобы показать'}
    >
      {children}
    </span>
  )
}

export function RichText({ text, onMention }: { text: string; onMention?: (name: string) => void }) {
  return (
    <>
      {tokenize(text).map((token, index) => {
        const key = index

        switch (token.kind) {
          case 'bold':
            return <strong key={key}>{token.value}</strong>
          case 'italic':
            return <em key={key}>{token.value}</em>
          case 'strike':
            return <s key={key}>{token.value}</s>
          case 'code':
            return <code key={key} className="inline-code">{token.value}</code>
          case 'pre':
            return <pre key={key} className="code-block">{token.value.replace(/^\n/, '')}</pre>
          case 'spoiler':
            return <Spoiler key={key}>{token.value}</Spoiler>
          case 'link':
            return (
              <a
                key={key}
                href={token.href}
                target="_blank"
                /* noopener обязателен: без него открытая страница получает
                   доступ к нашей вкладке и может её подменить */
                rel="noopener noreferrer"
                className="msg-link"
              >
                {token.value}
              </a>
            )
          case 'mention':
            return (
              <button
                key={key}
                className="mention"
                onClick={() => onMention?.(token.value)}
                type="button"
              >
                @{token.value}
              </button>
            )
          default:
            return <Fragment key={key}>{token.value}</Fragment>
        }
      })}
    </>
  )
}

/** Есть ли в тексте упоминание конкретного человека. */
export function mentions(text: string, displayName: string): boolean {
  const first = displayName.trim().split(/\s+/)[0]?.toLowerCase()
  if (!first) return false
  return new RegExp(`(?<![\\wа-яё@])@${first}(?![\\wа-яё])`, 'i').test(text)
}
